/**
 * Tenant-scoped browser session helpers (active project cache, logout clears).
 */

export const ACTIVE_PROJECT_STORAGE_KEY = 'activeProject';
export const TENANT_SESSION_CHANGED_EVENT = 'archon-tenant-session-changed';

/** Demo rename (DB updated 2026); stale localStorage may still show 800 km in the footer. */
export const LEGACY_SELVA_VERDE_PROJECT_NAME = 'Selva Verde 800 km 2D Seismic Survey';
export const CANONICAL_SELVA_VERDE_PROJECT_NAME = 'Selva Verde 200 km 2D Seismic Survey';
export const SELVA_VERDE_DEMO_JOB_NUMBER = 'SV-800-2026-2D';

export function normalizeLegacyActiveProject(project) {
  if (!project || typeof project !== 'object') return project;
  const isSelvaVerde =
    project.job_number === SELVA_VERDE_DEMO_JOB_NUMBER ||
    String(project.project_name || '').includes('Selva Verde');
  if (!isSelvaVerde) return project;
  if (
    project.project_name === LEGACY_SELVA_VERDE_PROJECT_NAME ||
    String(project.project_name || '').includes('800 km')
  ) {
    return { ...project, project_name: CANONICAL_SELVA_VERDE_PROJECT_NAME };
  }
  return project;
}

/** 48 hours — must match ActiveProjectContext */
export const ACTIVE_PROJECT_CACHE_MS = 48 * 60 * 60 * 1000;

export function normalizeTenantSlug(value) {
  if (value == null || value === '') return null;
  return String(value).trim().toLowerCase();
}

export function emitTenantSessionChanged(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(TENANT_SESSION_CHANGED_EVENT, { detail }),
  );
}

export function clearActiveProjectStorage() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Clear tenant-bound client state and notify ActiveProjectContext. */
export function clearTenantScopedClientState(reason = 'tenant-session-clear') {
  clearActiveProjectStorage();
  emitTenantSessionChanged({ reason });
}

export function readActiveProjectCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Load cached active project only when it belongs to the current tenant session.
 * Returns null when logged out, tenant mismatch, expired cache, or missing tenant stamp.
 */
export function loadActiveProjectForTenant(currentTenantSlug) {
  const current = normalizeTenantSlug(currentTenantSlug);
  const cached = readActiveProjectCache();
  if (!cached) return null;

  const { project, timestamp, tenantId } = cached;
  const elapsed = Date.now() - (timestamp || 0);
  if (elapsed > ACTIVE_PROJECT_CACHE_MS) {
    clearActiveProjectStorage();
    return null;
  }

  const storedTenant = normalizeTenantSlug(tenantId);
  if (!current || !storedTenant || storedTenant !== current) {
    clearActiveProjectStorage();
    return null;
  }

  if (!project || typeof project !== 'object') {
    clearActiveProjectStorage();
    return null;
  }

  return normalizeLegacyActiveProject({ ...project, _tenantSlug: storedTenant });
}

export function persistActiveProjectForTenant(project, currentTenantSlug) {
  if (typeof window === 'undefined') return;
  const tenant = normalizeTenantSlug(currentTenantSlug);
  try {
    if (!project || !tenant) {
      clearActiveProjectStorage();
      return;
    }
    const payload = {
      project: normalizeLegacyActiveProject({ ...project, _tenantSlug: tenant }),
      timestamp: Date.now(),
      tenantId: tenant,
    };
    window.localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Error saving active project to localStorage', error);
  }
}

export function isActiveProjectForCurrentTenant(project, currentTenantSlug) {
  if (!project) return false;
  const current = normalizeTenantSlug(currentTenantSlug);
  const projectTenant = normalizeTenantSlug(project._tenantSlug);
  return Boolean(current && projectTenant && projectTenant === current);
}
